import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { AppShell } from "@/components/AppShell";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RequireAccountRole } from "@/components/RequireAccountRole";
import Login from "@/pages/Login";
import NotFound from "@/pages/NotFound";
import Home from "@/pages/Home";
import { useAuth } from "@/lib/auth";

// Every page past the public landing/login is a separate chunk so a tenant
// viewer never downloads the admin or super-admin surfaces.
const DesignGallery = lazy(() =>
  import("@/pages/DesignGallery").then((m) => ({ default: m.DesignGallery })),
);
const Register = lazy(() => import("@/pages/Register"));
const ChangePassword = lazy(() => import("@/pages/ChangePassword"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const AccountMembers = lazy(() => import("@/pages/AccountMembers"));
const AccountSettings = lazy(() => import("@/pages/AccountSettings"));
const AccountSpamOverrides = lazy(() => import("@/pages/AccountSpamOverrides"));
const SuperAdminAccounts = lazy(() => import("@/pages/superadmin/Accounts"));
const SuperAdminUsers = lazy(() => import("@/pages/superadmin/Users"));
const SuperAdminWallets = lazy(() => import("@/pages/superadmin/Wallets"));
const SuperAdminTransactions = lazy(
  () => import("@/pages/superadmin/Transactions"),
);
const SuperAdminApiKeys = lazy(() => import("@/pages/superadmin/ApiKeys"));
const SuperAdminZohoOAuth = lazy(() => import("@/pages/superadmin/ZohoOAuth"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Wallets = lazy(() => import("@/pages/Wallets"));
const WalletDetail = lazy(() => import("@/pages/WalletDetail"));
const TokenWalletDetail = lazy(() => import("@/pages/TokenWalletDetail"));
const TransactionsGlobal = lazy(() => import("@/pages/TransactionsGlobal"));
const SyncJobs = lazy(() => import("@/pages/SyncJobs"));
const SettingsGeneral = lazy(() => import("@/pages/Settings"));
const SettingsLayout = lazy(() =>
  import("@/pages/Settings").then((m) => ({ default: m.SettingsLayout })),
);
const SettingsIntegrations = lazy(() =>
  import("@/pages/Settings").then((m) => ({ default: m.SettingsIntegrations })),
);
const AdminChainsTokens = lazy(() => import("@/pages/AdminChainsTokens"));
const AdminTokens = lazy(() => import("@/pages/AdminTokens"));
const AdminDeferred = lazy(() => import("@/pages/AdminDeferred"));
const AdminLogViewer = lazy(() => import("@/pages/AdminLogViewer"));

/**
 * Route table.
 *
 * Public: /login, /register, /design (dev gallery), and the catch-all 404.
 * Forced: /change-password — authed, but ProtectedRoute sends callers with
 *   `mustChangePassword=true` here on every route until they complete it.
 * Protected: everything inside <AppShell />.
 * Settings hub: /settings/* is one route family behind a persistent section
 *   rail (see Settings.tsx). The index (General) is open to any member; the
 *   Account / Members / Spam overrides / Integrations children wrap in
 *   <RequireAccountRole role="ADMIN">. Super admin passes unconditionally.
 *   The old /account/settings, /account/members, and /account-settings/
 *   spam-overrides paths redirect into the hub (the avatar menu, a dashboard
 *   link, and the hard-coded Zoho OAuth callback still target them).
 * Hidden: /_superadmin/accounts — super admin only, no nav link.
 *
 * `<ToastProvider>` lives in `main.tsx` at the app root so both authenticated
 * and unauthenticated surfaces can call `useToast()` without double-mounting.
 */
export function AppRoutes() {
  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/design" element={<DesignGallery />} />

        <Route
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          {/* Change-password lives inside the shell (sidebar visible) but
            BYPASSES the mustChangePassword gate — otherwise the gate would
            redirect its own target and loop. Putting it in the shell means
            the user always has a logout affordance even before completing
            password change. */}
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/wallets" element={<Wallets />} />
          <Route path="/wallets/:id" element={<WalletDetail />} />
          <Route path="/token-wallets/:id" element={<TokenWalletDetail />} />
          <Route path="/transactions" element={<TransactionsGlobal />} />
          <Route path="/sync-jobs" element={<SyncJobs />} />
          {/* Settings hub. Persistent section rail + the active section's
            cards. Admin-only sections are guarded here AND hidden from the
            rail for non-admins. */}
          <Route path="/settings" element={<SettingsLayout />}>
            <Route index element={<SettingsGeneral />} />
            <Route
              path="account"
              element={
                <RequireAccountRole role="ADMIN">
                  <AccountSettings />
                </RequireAccountRole>
              }
            />
            <Route
              path="members"
              element={
                <RequireAccountRole role="ADMIN">
                  <AccountMembers />
                </RequireAccountRole>
              }
            />
            <Route
              path="spam-overrides"
              element={
                <RequireAccountRole role="ADMIN">
                  <AccountSpamOverrides />
                </RequireAccountRole>
              }
            />
            <Route
              path="integrations"
              element={
                <RequireAccountRole role="ADMIN">
                  <SettingsIntegrations />
                </RequireAccountRole>
              }
            />
          </Route>

          {/* Legacy Settings paths → hub redirects. The avatar menu, a dashboard
            link, and the hard-coded Zoho OAuth callback still target these. */}
          <Route
            path="/account/settings"
            element={<LegacyAccountSettingsRedirect />}
          />
          <Route
            path="/account/members"
            element={<Navigate to="/settings/members" replace />}
          />
          <Route
            path="/account-settings/spam-overrides"
            element={<Navigate to="/settings/spam-overrides" replace />}
          />
          <Route path="/admin/logs" element={<AdminLogViewer />} />
          <Route path="/admin/chains-tokens" element={<AdminChainsTokens />} />
          <Route
            path="/admin/tokens"
            element={
              <SuperAdminGate>
                <AdminTokens />
              </SuperAdminGate>
            }
          />
          <Route path="/admin/:section" element={<AdminDeferred />} />

          {/* Super-admin surface — also inside the shell so admins always have
            the same sidebar (and logout) as any other user. The SuperAdminGate
            is unchanged; only the chrome moved. */}
          <Route
            path="/_superadmin/accounts"
            element={
              <SuperAdminGate>
                <SuperAdminAccounts />
              </SuperAdminGate>
            }
          />
          <Route
            path="/_superadmin/users"
            element={
              <SuperAdminGate>
                <SuperAdminUsers />
              </SuperAdminGate>
            }
          />
          <Route
            path="/_superadmin/wallets"
            element={
              <SuperAdminGate>
                <SuperAdminWallets />
              </SuperAdminGate>
            }
          />
          <Route
            path="/_superadmin/transactions"
            element={
              <SuperAdminGate>
                <SuperAdminTransactions />
              </SuperAdminGate>
            }
          />
          <Route
            path="/_superadmin/api-keys"
            element={
              <SuperAdminGate>
                <SuperAdminApiKeys />
              </SuperAdminGate>
            }
          />
          <Route
            path="/_superadmin/zoho-oauth"
            element={
              <SuperAdminGate>
                <SuperAdminZohoOAuth />
              </SuperAdminGate>
            }
          />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

/**
 * Legacy `/account/settings` → hub redirect. The Zoho OAuth callback hard-codes
 * a redirect to `${appBaseUrl}/account/settings?zoho=…` (apps/api
 * zoho/routes/connections.ts), so we must preserve the query string and route
 * a Zoho callback to the Integrations section — where the callback effect that
 * surfaces the toast and strips the query lives. A plain avatar-menu click
 * (no `zoho` param) lands on the Account section.
 */
function LegacyAccountSettingsRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const target = params.has("zoho")
    ? "/settings/integrations"
    : "/settings/account";
  return (
    <Navigate to={{ pathname: target, search: location.search }} replace />
  );
}

/**
 * Minimal guard for the hidden super-admin surface. Sits alongside the
 * backend requireSuperAdmin middleware — the UI hides the surface; the
 * server enforces it.
 */
function SuperAdminGate({ children }: { children: ReactNode }) {
  const { isSuperAdmin } = useAuth();
  if (!isSuperAdmin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}
